import { createApiClient, ApiService } from '@onetasklist/shared';
import { useContext, useMemo } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { ApiContext } from '@contexts/ApiContext';
import { useConfig } from '@contexts/ConfigContext';

/**
 * Hook to get the configured API service
 * Uses the shared package with web-specific token providers
 * 
 * Important: The Region API baseURL must be the regionBaseUrl from config,
 * not just '/api'. The API client will append '/api' to the base URL.
 * 
 * Example:
 * - regionBaseUrl = 'https://region.example.com'
 * - API call to boards: 'https://region.example.com/api/boards'
 */
export const useApi = (): ApiService => {
  const apiFromContext = useContext(ApiContext);
  const { getAccessToken } = useAuth();
  const { config } = useConfig();

  const api = useMemo(() => {
    // Use regionBaseUrl from config, or empty string for same-origin
    const baseURL = config?.regionBaseUrl || '';

    const apiClient = createApiClient({
      baseURL,  // This is the regionBaseUrl (e.g., 'https://region.example.com' or '')
      tokenProvider: {
        getAccessToken,
      },
      withCredentials: false,
      onUnauthorized: () => {
        // Dispatch custom event for unauthorized
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      },
      onForbidden: () => {
        // Dispatch custom event for forbidden
        window.dispatchEvent(new CustomEvent('auth:forbidden'));
      },
    });

    return new ApiService(apiClient);
  }, [getAccessToken, config?.regionBaseUrl]);

  if (apiFromContext) {
    return apiFromContext;
  }

  return api;
};
